import React, { ChangeEvent, useEffect, useReducer } from 'react'
import { styled } from '@mui/material/styles'
import {
  Button as MuiButton,
  Grid as MuiGrid,
  TextField as MuiTextField,
  CardHeader,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider as MuiDivider,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableRow,
  Box,
  Stack,
  Tooltip
} from '@mui/material'
import * as Yup from 'yup'
import { spacing } from '@mui/system'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import { RootState } from '../../redux'
import { getLevelsByProjectId } from '../../redux/slices/levels'
import { getLiftsByProjectId } from '../../redux/slices/lifts'
import { getPowerSuppliesByProjectId, updatePowerSupply } from '../../redux/slices/powerSupplies'
import { setData, setStep } from '../../redux/slices/steps'
import { setDevice } from '../../redux/slices/devices'
import { QRData } from '../../enums/QRData'
import powerSupplyService from '../../services/powerSupplyService'
import { updateAsset } from '../../services/assetService'
import { createErrorOrSuccessNotification } from '../../redux/slices/notifications'
import { NotificationType } from '../../enums/NotificationType'
import { deleteError, setError } from '../../redux/slices/errors'
import { PowerSupplyErrors } from '../../enums/PowerSupplyErrors'
import useAuth from '../../hooks/useAuth'
import { createUserNotification } from '../../utils/createUserNotification'
import { ActionRequest, Device, PowerSupply, PowerSupplyInput, Project } from '../../types'
import { ProductType } from '../../enums/ProductType'
import { deleteDeviceLocationChangeRequest } from '../../redux/slices/requests'
import { updateRequestState } from '../../services/requestService'
import { RequestStatus } from '../../enums/RequestStatus'
import { getActionRequestsByProject } from '../../services/projectService'
import { RequesterType } from '../../enums/RequesterType'
import productService from '../../services/productService'
import NominalCurrent from '../../enums/NominalCurrent'
import { NominalCurrentSelect100And250 } from '../NominalCurrentSelect'
import InfoIcon from '@mui/icons-material/Info'
import { PowerSupplyUpdateAction } from '../../enums'
import { translateProductType } from '../../utils/product'
import { Add } from '@mui/icons-material'
import InputCablesAutocomplete from '../InputCablesAutocomplete'
import FuseAutocomplete from '../FuseAutocomplete'
import { withUpdatedPowerSupplyAsset } from '../../utils/powerSupply'

/**
 * The styled TextField
 */
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)

/**
 * The styled Button
 */
const Button = styled(MuiButton)(spacing)

/**
 * The styled Grid
 */
const Grid = styled(MuiGrid)(spacing)

/**
 * The styled Divider
 */
const Divider = styled(MuiDivider)(spacing)

/**
 * The properties
 */
interface PowerSupplyFormProps {
  device: Device
  project: Project
  initialLevel?: number | null
  initialLift?: number | null
  onCancel?: () => void
}

type PowerSupplyFormValues = PowerSupplyInput & {
  id: number
  action: PowerSupplyUpdateAction
}

export default function PowerSupplyForm({
  device,
  project,
  initialLevel,
  initialLift,
  onCancel
}: PowerSupplyFormProps) {
  return (
    <PowerSupplyFormContent
      device={device}
      powerSupply={device.powerSupply}
      project={project}
      initialLevel={initialLevel}
      initialLift={initialLift}
      onCancel={onCancel}
    />
  )
}

type PowerSupplyFormContentProps = PowerSupplyFormProps & {
  powerSupply?: Device['powerSupply']
}

type PowerSupplyFormState = {
  nominalCurrent: string
  openCurrentTransformerInfo: boolean
}

type PowerSupplyFormAction =
  | {
      type: 'setNominalCurrent'
      nominalCurrent: string
    }
  | {
      type: 'setOpenCurrentTransformerInfo'
      open: boolean
    }

function powerSupplyFormReducer(
  state: PowerSupplyFormState,
  action: PowerSupplyFormAction
): PowerSupplyFormState {
  switch (action.type) {
    case 'setNominalCurrent':
      return {
        ...state,
        nominalCurrent: action.nominalCurrent
      }
    case 'setOpenCurrentTransformerInfo':
      return {
        ...state,
        openCurrentTransformerInfo: action.open
      }
    default:
      return state
  }
}

function getInitialNominalCurrent(voltage1Tp?: number | null): string {
  switch (voltage1Tp) {
    case 1000:
      return NominalCurrent.NOMINAL100 as string
    case 2500:
      return NominalCurrent.NOMINAL250 as string
    case 4000:
      return NominalCurrent.NOMINAL400 as string
    case 6000:
      return NominalCurrent.NOMINAL600 as string
    default:
      return NominalCurrent.OTHER as string
  }
}

function isFuseValidationEnabled(productType?: ProductType): boolean {
  return (
    productType === ProductType.PLUG_AND_PLAY ||
    productType === ProductType.INSTALLATION_KIT ||
    productType === ProductType.SPINE_MODULE
  )
}

function PowerSupplyFormContent({
  device,
  powerSupply,
  project,
  initialLevel,
  initialLift,
  onCancel
}: PowerSupplyFormContentProps) {
  const hasPowerSupply = powerSupply !== null && powerSupply !== undefined

  /**
   * The dispatch function
   */
  const dispatch = useDispatch()

  /**
   * The translate function
   */
  const [t] = useTranslation('common')

  /**
   * The steps of installation process
   */
  const { step } = useSelector((state: RootState) => state.steps)
  /**
   * The levels of project
   */
  const { levels } = useSelector((state: RootState) => state.levels)
  /**
   * The lifts of project
   */
  const { lifts } = useSelector((state: RootState) => state.lifts)
  /**
   * The powerSupplies
   */
  const { powerSupplies } = useSelector((state: RootState) => state.powerSupplies)

  const [state, dispatchState] = useReducer(powerSupplyFormReducer, {
    nominalCurrent: getInitialNominalCurrent(powerSupply?.voltage_1_tp),
    openCurrentTransformerInfo: false
  })

  /**
   * Is device type allowed to change fuse size
   */
  const useFuseValidation = hasPowerSupply && isFuseValidationEnabled(device.product?.type)
  const showInputCablesField = hasPowerSupply && device.product?.type !== ProductType.SPINE_MODULE

  /**
   * The validation schema
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      t('isRequired', {
        field: t('name')
      })
    ),
    level: Yup.number().nullable(),
    lift: Yup.number().nullable(),
    ...(showInputCablesField
      ? {
          inputCables: Yup.number().required(
            t('isRequired', {
              field: t('inputCables').toLowerCase()
            })
          )
        }
      : {}),
    ...(useFuseValidation
      ? {
          fuse: Yup.number()
            .positive()
            .required(t('isRequired', { field: t('fuse') }))
        }
      : {})
  })

  /**
   * Initial values
   */
  const initialValues: PowerSupplyFormValues = {
    id: powerSupply?.id ?? 0,
    name: '',
    level: device.asset?.levelId ?? initialLevel ?? null,
    lift: device?.asset?.liftId ?? initialLift ?? null,
    parent: powerSupply?.parentId ?? null,
    isMainDistributionAssembly: powerSupply?.isMainDistributionAssembly ?? false,
    fuse: powerSupply?.fuse ?? 0,
    inputCables: powerSupply?.inputCables ?? 1,
    project: device?.asset?.project ? device?.asset?.project.id : project.id,
    voltage_1_tp: powerSupply?.voltage_1_tp ?? 0,
    voltage_1_ts: powerSupply?.voltage_1_ts ?? 0,
    action: PowerSupplyUpdateAction.INSTALL,
    description: null
  }

  useEffect(() => {
    dispatch(getLevelsByProjectId(project.id))
    dispatch(getLiftsByProjectId(project.id))
    if (hasPowerSupply) {
      dispatch(getPowerSuppliesByProjectId(project.id))
    }
  }, [dispatch, hasPowerSupply, project.id])

  /**
   * Main powersupplies
   */
  const mainPowerSupplies = powerSupplies.filter(
    (candidate: PowerSupply) =>
      candidate.isMainDistributionAssembly && candidate.id !== powerSupply?.id
  )

  /**
   * The current user
   */
  const { currentUser } = useAuth()

  async function getDeviceRequests(): Promise<ActionRequest[]> {
    const projectId = device.asset?.project?.id

    if (projectId === undefined) {
      return []
    }

    return await getActionRequestsByProject(projectId, {
      requesterType: RequesterType.DEVICE,
      requesterId: device.name
    })
  }

  const handleSubmit = async (
    { id, owner, project, level, lift, ...powerSupplyInput }: PowerSupplyFormValues,
    { resetForm, setSubmitting }: FormikHelpers<PowerSupplyFormValues>
  ) => {
    try {
      const deviceRequests = await getDeviceRequests()

      /*Make parent id null if user marks current powersupply as main distribution assembly.
      This check is made to prevent error situation where user accidentally selects parent for powersupply before checking
      this powersupply as main distribution assembly.*/
      if (
        powerSupplyInput.isMainDistributionAssembly === true &&
        powerSupplyInput.parent !== null
      ) {
        if (level === null) {
          level = null
          lift = null
        }
        powerSupplyInput.parent = null
      }

      if (level === null) {
        lift = null
      }

      let updatedPowerSupply: PowerSupply | undefined = undefined

      if (hasPowerSupply) {
        updatedPowerSupply = await powerSupplyService.updatePowerSupply(id, powerSupplyInput)
      }

      if (device.asset) {
        const updatedAsset = await updateAsset(device.asset.id, {
          name: powerSupplyInput.name ?? device.asset.name,
          description:
            powerSupplyInput.description !== undefined
              ? powerSupplyInput.description
              : (device.asset.description ?? null),
          company: owner !== undefined ? owner : (device.asset.companyId ?? null),
          project: project !== undefined ? project : (device.asset.projectId ?? null),
          level: level !== undefined ? level : (device.asset.levelId ?? null),
          lift: lift !== undefined ? lift : (device.asset.liftId ?? null)
        })

        if (updatedPowerSupply) {
          updatedPowerSupply = withUpdatedPowerSupplyAsset(updatedPowerSupply, updatedAsset)
        }
      }

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.SUCCESS,
          hasPowerSupply ? t('powerSupplyUpdateSuccess') : t('installation:installSuccess')
        )
      )

      if (updatedPowerSupply) {
        dispatch(updatePowerSupply(updatedPowerSupply))
      }

      if (device.product?.type === ProductType.INSTALLATION_KIT) {
        //Update product type from installation kit to smart electrical cabinet
        await productService.updateProduct(device.product.id, {
          type: ProductType.SMART_ELECTRICAL_CABINET
        })
      }

      if (deviceRequests.length > 0) {
        await handleRequestsStatusUpdates(deviceRequests, RequestStatus.APPROVED)
      }
      dispatch(setStep(step + 3))
      dispatch(setDevice(undefined))
      dispatch(setData(QRData.NOT_SCANNED))
      resetForm()
    } catch (error: unknown) {
      dispatch(
        setError({
          type: PowerSupplyErrors.EDIT,
          error: error
        })
      )

      //Create message
      const errorMessage = createUserNotification({
        user: currentUser,
        type: PowerSupplyErrors.EDIT,
        error: error
      })

      //Dispatch error message
      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(PowerSupplyErrors.EDIT))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    onCancel?.()
  }

  async function handleRequestsStatusUpdates(
    deviceRequests: ActionRequest[],
    status: RequestStatus
  ): Promise<void> {
    await Promise.all(
      deviceRequests.map(async (request: ActionRequest) => {
        const updatedRequest = await updateRequestState(request.id, {
          status: status,
          approver: currentUser?.firstName + ' ' + currentUser?.lastName
        })
        dispatch(deleteDeviceLocationChangeRequest(updatedRequest.id))
      })
    )
  }

  return (
    <React.Fragment>
      {/**The currentTransformer info popup */}
      <Dialog
        open={state.openCurrentTransformerInfo}
        onClose={() =>
          dispatchState({
            type: 'setOpenCurrentTransformerInfo',
            open: false
          })
        }
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('currentTransformerInfoTitle')}</DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Table>
              <TableRow>
                {t('currentTransformer100A') + ': ' + 'SCT016 100 A : 0,1 A (100 mA), 1%'}
              </TableRow>
              <TableRow>
                {t('currentTransformer250A') + ': ' + 'SCT024 250 A : 0,1 A (100 mA), 1%'}
              </TableRow>
              <TableRow>{t('other') + ': ' + t('currentTransformerOtherInfo')}</TableRow>
            </Table>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() =>
              dispatchState({
                type: 'setOpenCurrentTransformerInfo',
                open: false
              })
            }
            autoFocus
          >
            {t('understood')}
          </Button>
        </DialogActions>
      </Dialog>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setValues }) => (
          <form onSubmit={handleSubmit}>
            <CardHeader
              title={
                hasPowerSupply
                  ? t('Manufacturing.instructionsForPowerSupply')
                  : t('installation:enterDeviceDetails')
              }
              align="center"
              titleTypographyProps={{ variant: 'h6' }}
            />

            {(device?.product?.type === ProductType.INSTALLATION_KIT ||
              device?.product?.type === ProductType.PLUG_AND_PLAY) && (
              <Alert severity="warning">
                {`${t('installationTypeNotification')} ${translateProductType(
                  device.product.type
                )}`}
              </Alert>
            )}

            <Divider sx={{ my: 6 }} />

            <Grid container spacing={6}>
              <Grid item xs={12}>
                <TextField
                  type="text"
                  name="name"
                  label={t('Manufacturing.name')}
                  value={values.name}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid item container spacing={6}>
                  <Grid item xs={12}>
                    <Alert severity="info">{t('areaHelperText')}</Alert>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      name="level"
                      label={t('Manufacturing.level')}
                      value={values.level}
                      error={Boolean(touched.level && errors.level)}
                      helperText={touched.level && errors.level}
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    >
                      {levels.map((level) => (
                        <MenuItem key={level.id} value={level.id}>
                          {level.name}
                        </MenuItem>
                      ))}
                      <Divider />
                      <Box sx={{ px: 4, pt: 1 }}>
                        <Button
                          variant="text"
                          startIcon={<Add fontSize="inherit" />}
                          size="small"
                          onClick={() => dispatch(setStep(step + 1))}
                        >
                          {t('newLevel')}
                        </Button>
                      </Box>
                    </TextField>

                    {values.level !== null && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setValues({
                              ...values,
                              level: null,
                              lift: null
                            })
                          }}
                        >
                          {t('clearSelection')}
                        </Button>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={6}>
                      <Grid item xs={8} sm={8} md={8} lg={8} xl>
                        <TextField
                          select
                          name="lift"
                          label={t('Manufacturing.lift')}
                          disabled={values.level === null}
                          value={values.lift}
                          error={Boolean(touched.lift && errors.lift)}
                          helperText={touched.lift && errors.lift}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          {lifts.map((lift) => (
                            <MenuItem key={lift.id} value={lift.id}>
                              {lift.name}
                            </MenuItem>
                          ))}
                          <Divider />
                          <Box sx={{ px: 4, pt: 1 }}>
                            <Button
                              variant="text"
                              startIcon={<Add fontSize="inherit" />}
                              size="small"
                              onClick={() => dispatch(setStep(step + 2))}
                            >
                              {t('newLift')}
                            </Button>
                          </Box>
                        </TextField>

                        {values.lift !== null && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end'
                            }}
                          >
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                setValues({
                                  ...values,
                                  lift: null
                                })
                              }}
                            >
                              {t('clearSelection')}
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {useFuseValidation && (
                <Grid item xs={12} md={6}>
                  <FuseAutocomplete
                    value={values.fuse}
                    error={Boolean(touched.fuse && errors.fuse)}
                    helperText={touched.fuse && errors.fuse}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={(fuse) => {
                      setValues({
                        ...values,
                        fuse
                      })
                    }}
                  />

                  {/*
                      <PowerSupplyFuseSelect
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                sx={{ p: '10px' }}
                                aria-label="menu"
                                onClick={handleClickShowFuseSelect}
                              >
                                {showFuseSelect ? (
                                  <TextFieldsIcon />
                                ) : (
                                  <MenuIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment:
                            showFuseSelect === false ? (
                              <InputAdornment
                                position="end"
                                style={{ color: 'white' }}
                              >
                                A
                              </InputAdornment>
                            ) : (
                              ''
                            )
                        }}
                        select={showFuseSelect}
                        value={values.fuse}
                        error={Boolean(touched.fuse && errors.fuse)}
                        helperText={touched.fuse && errors.fuse}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      */}
                </Grid>
              )}

              {showInputCablesField && (
                <Grid item xs={12} md={6}>
                  <InputCablesAutocomplete
                    value={values.inputCables}
                    error={Boolean(touched.inputCables && errors.inputCables)}
                    helperText={touched.inputCables && errors.inputCables}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={(inputCables) => {
                      setValues({
                        ...values,
                        inputCables
                      })
                    }}
                  />

                  {/*<PowerSupplyInputCablesSelect
                        value={values.inputCables}
                        error={Boolean(
                          touched.inputCables && errors.inputCables
                        )}
                        helperText={touched.inputCables && errors.inputCables}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />*/}
                </Grid>
              )}

              {hasPowerSupply && device?.product?.type === ProductType.INSTALLATION_KIT && (
                /**
                 * Nominalcurrent select for installationKit
                 */
                <Grid item xs={12}>
                  <Grid container spacing={6}>
                    <Grid item xs={4}>
                      <Stack direction="row" alignItems="center" gap={3}>
                        <Tooltip title={t('info')}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              dispatchState({
                                type: 'setOpenCurrentTransformerInfo',
                                open: true
                              })
                            }
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <NominalCurrentSelect100And250
                          name="nominalCurrent"
                          value={state.nominalCurrent}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={(event) => {
                            const nominalCurrent = event.target.value

                            dispatchState({
                              type: 'setNominalCurrent',
                              nominalCurrent
                            })

                            if (nominalCurrent === NominalCurrent.NOMINAL100) {
                              setValues({
                                ...values,
                                voltage_1_tp: 1000,
                                voltage_1_ts: 1
                              })
                              return
                            }

                            if (nominalCurrent === NominalCurrent.NOMINAL250) {
                              setValues({
                                ...values,
                                voltage_1_tp: 2500,
                                voltage_1_ts: 1
                              })
                            }
                          }}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        name="voltage_1_tp"
                        sx={{
                          display:
                            state.nominalCurrent === NominalCurrent.OTHER ? 'default' : 'none'
                        }}
                        label={t('PowerSupply.voltage_1_tp')}
                        value={values.voltage_1_tp}
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        name="voltage_1_ts"
                        sx={{
                          display:
                            state.nominalCurrent === NominalCurrent.OTHER ? 'default' : 'none'
                        }}
                        label={t('PowerSupply.voltage_1_ts')}
                        value={values.voltage_1_ts}
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {hasPowerSupply && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={t('PowerSupply.isMainDistributionAssembly')}
                        control={
                          <Checkbox
                            name="isMainDistributionAssembly"
                            checked={values.isMainDistributionAssembly}
                            onChange={(event: ChangeEvent) => {
                              setValues({
                                ...values,
                                isMainDistributionAssembly:
                                  // @ts-expect-error typed incorrectly
                                  event.target.checked,
                                parent: null
                              })
                            }}
                          />
                        }
                      />
                    </Grid>

                    {values.isMainDistributionAssembly === false && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          name="parent"
                          label={t('Manufacturing.parentName')}
                          value={values.parent}
                          error={Boolean(touched.parent && errors.parent)}
                          helperText={touched.parent && errors.parent}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          fullWidth
                        >
                          {mainPowerSupplies.map((powerSupply) => (
                            <MenuItem key={powerSupply.id} value={powerSupply.id}>
                              {powerSupply.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={3}>
                  <Button variant="text" color="inherit" onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    {t('save')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </React.Fragment>
  )
}
